import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import { ChartModule, UIChart } from 'primeng/chart';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { TaskCycleTimeBarSeries } from '../task-cycle-time-heat-map/task-cycle-time-heat-map.component';

const MONTH_COLORS = [
    '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
];

@Component({
    selector: 'task-six-month-bar-chart',
    templateUrl: './task-six-month-bar-chart.component.html',
    styleUrl: './task-six-month-bar-chart.component.scss',
    imports: [SharedModule, ChartModule, PaginatorModule, SelectModule]
})
export class TaskSixMonthBarChartComponent implements OnChanges {
    @ViewChild('chart') chart: UIChart;

    @Input() series: TaskCycleTimeBarSeries[] = [];
    @Input() monthLabels: string[] = [];
    @Input() isExpanded = false;
    @Input() chartHeight = '226px';
    @Input() collapsedChartHeight = '250px';

    first2 = 0;
    rows2 = 5;
    options = [
        { label: '5', value: 5 },
        { label: '10', value: 10 }
    ];

    legendItems: { label: string; color: string; index: number; hidden: boolean }[] = [];
    hiddenMonths = new Set<number>();

    get displayTasks(): TaskCycleTimeBarSeries[] {
        return this.series.slice(this.first2, this.first2 + this.rows2);
    }

    // Y-axis unit chosen per render: hours when all bars are under a day, otherwise days
    yAxisUnit: 'hours' | 'days' = 'days';

    chartData: any = {
        labels: [],
        datasets: []
    };

    chartOptions = {
        animation: {
            duration: 0
        },
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                mode: 'nearest',
                intersect: false,
                axis: 'x',
                usePointStyle: true,
                borderWidth: 0,
                callbacks: {
                    label: (context: any) => {
                        const days = this.yAxisUnit === 'hours' ? context.raw / 24 : context.raw;
                        const formatted = this.formatHoursToDayHour(days);
                        const name = context.dataset?.label;
                        return name ? `${name}: ${formatted}` : formatted;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    display: true,
                    font: {
                        size: 12
                    },
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    callback: (_value: any, index: number) => {
                        const label = this.chartData?.labels?.[index] ?? '';
                        return this.abbreviate(label);
                    }
                }
            },
            y: {
                position: 'right',
                beginAtZero: true,
                ticks: {
                    font: {
                        size: 12
                    },
                    precision: 0,
                    callback: (value: number) => {
                        if (value === 0) return '0';
                        if (this.yAxisUnit === 'hours') return value + (value === 1 ? ' hour' : ' hours');
                        return value + (value === 1 ? ' day' : ' days');
                    }
                },
                border: {
                    display: false
                }
            }
        }
    };

    constructor() { }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges['series']) {
            this.first2 = 0;
        }
        if (simpleChanges['series'] || simpleChanges['monthLabels']) {
            this.updateChartData();
        }
    }

    onRowsChange(): void {
        this.first2 = 0;
        this.updateChartData();
    }

    onPageChange2(event: PaginatorState): void {
        this.first2 = event.first ?? 0;
        this.rows2 = event.rows ?? this.rows2;
        this.updateChartData();
    }

    updateChartData(): void {
        const tasks = this.displayTasks;
        // Pad to a constant category count so bar width and axis position stay identical across pages
        const slots = Array.from({ length: this.rows2 }, (_, i) => tasks[i]);

        this.legendItems = this.monthLabels.map((month, monthIndex) => ({
            label: month,
            color: MONTH_COLORS[monthIndex % MONTH_COLORS.length],
            index: monthIndex,
            hidden: this.hiddenMonths.has(monthIndex)
        }));

        const visibleMonths = this.monthLabels
            .map((month, monthIndex) => ({ month, monthIndex }))
            .filter(m => !this.hiddenMonths.has(m.monthIndex));

        // task data is in hours; show hours when every visible bar is under a day
        const maxHours = Math.max(0, ...slots.flatMap(t => t ? visibleMonths.map(m => t.data[m.monthIndex] ?? 0) : []));
        this.yAxisUnit = maxHours < 24 ? 'hours' : 'days';
        // hours mode rounds up to whole hours so sub-hour bars match the tooltip
        const scale = (h: number) => this.yAxisUnit === 'hours' ? Math.ceil(h) : h / 24;

        this.chartData = {
            labels: slots.map(t => t?.label ?? ''),
            datasets: visibleMonths.map(({ month, monthIndex }) => ({
                label: month,
                data: slots.map(t => (t ? scale(t.data[monthIndex] ?? 0) : null)),
                backgroundColor: MONTH_COLORS[monthIndex % MONTH_COLORS.length],
                hoverBackgroundColor: MONTH_COLORS[monthIndex % MONTH_COLORS.length],
                borderRadius: 4,
                maxBarThickness: 24
            }))
        };
    }

    toggleLegendItem(index: number): void {
        if (this.hiddenMonths.has(index)) {
            this.hiddenMonths.delete(index);
        } else {
            this.hiddenMonths.add(index);
        }
        this.updateChartData();
    }

    hasHiddenDatasets(): boolean {
        return this.hiddenMonths.size > 0;
    }

    resetAllDatasets(): void {
        this.hiddenMonths.clear();
        this.updateChartData();
    }

    // Acronym of the task name, keeping trailing numbers so similar tasks stay distinct (e.g. "Issue Instructions 4" -> "II4")
    abbreviate(label: string): string {
        const words = (label ?? '').split(/[\s\-/()]+/).filter(Boolean);
        const abbr = words.map(w => /^\d+$/.test(w) ? w : w.charAt(0).toUpperCase()).join('');
        return abbr || label;
    }

    // value is expressed in days
    formatHoursToDayHour(value: number): string {
        if (value === 0) return '0';
        let label = '';
        const hours = Math.ceil(value * 24);
        const days = Math.floor(hours / 24);
        if (days > 0) {
            label += days + (days === 1 ? ' day' : ' days');
        }
        const remainingHours = hours % 24;
        if (remainingHours > 0) {
            if (label) label += ' ';
            label += remainingHours + (remainingHours === 1 ? ' hour' : ' hours');
        }
        return label;
    }
}
